import { AssessmentType, TechnologyUseSelectDto, TechnologyUseWithAssessmentsDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { Score } from '@components/shared/score.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { ChevronDownIcon, EditIcon } from 'lucide-react';
import React, { FunctionComponent, useState } from 'react';

type TechnologyUseItemProps = {
  isPrimary: boolean;
  onEdit?: () => void;
  technologyUses?: TechnologyUseWithAssessmentsDto[];
  use: TechnologyUseSelectDto;
};

export const TechnologyUseContainer: FunctionComponent<TechnologyUseItemProps> = (props) => {
  const { isPrimary, onEdit, technologyUses, use } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSubdomains, setExpandedSubdomains] = useState<Record<string, boolean>>({});

  const technologyUse = technologyUses?.find((techUse) => techUse.id === use.useId);
  const useTrl = technologyUse?.domains[0]?.featuredAssessment?.score;

  const toggleSubdomains = (domainId: string) => {
    setExpandedSubdomains((prev) => ({
      ...prev,
      [domainId]: !prev[domainId],
    }));
  };

  return (
    <div
      className="flex flex-col gap-2 bg-gray-100 h-fit rounded-md p-2"
      key={use.id}
    >
      <Tile
        className="p-2 rounded-md shadow-none"
        contentClassName="gap-2"
        hoverReveal={Boolean(onEdit)}
      >
        <div className="flex flex-row justify-between items-start gap-2">
          <div className="flex flex-row items-center min-w-0">
            <div className="flex flex-row items-center w-[52px] shrink-0">
              <Score
                score={useTrl}
                size="small"
                design="box"
                type="TRL"
              />
            </div>

            <TextWithLabel
              title={use.name}
              label="Use"
              className="text-sm"
              truncate
              badge={
                isPrimary ? (
                  <Badge
                    size="xs"
                    variant="success"
                  >
                    Primary
                  </Badge>
                ) : undefined
              }
            />
          </div>
          {onEdit && (
            <IconButton.HoverReveal
              icon={<EditIcon />}
              onClick={onEdit}
              variant="secondary"
              ariaLabel="Update Technology Use"
            />
          )}
        </div>
        {use.description && (
          <ExpandableText
            className="pl-[52px] text-xs text-gray-600"
            maxLines={3}
            text={use.description}
          />
        )}
        <div
          className="pl-[52px] flex justify-between items-center cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="text-xs font-normal text-[#1F1C1AB2]">
            {use.domains.length} Domain{use.domains.length === 1 ? '' : 's'}
          </div>
          <ChevronDownIcon
            size={20}
            className={cn('text-[#1F1C1AB2] transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </div>
      </Tile>

      {isOpen && use.domains.length > 0 && (
        <>
          {use.domains.map((domain, index) => {
            const technologyUse = technologyUses?.find((techUse) => techUse.id === use.useId);
            const domainWithAssessment = technologyUse?.domains.find(
              (techDomain) => techDomain.id === domain.id && techDomain.featuredAssessment?.type === AssessmentType.TRL,
            );
            const trl = domainWithAssessment?.featuredAssessment?.score;
            const subdomains =
              technologyUse?.domains.find((techDomain) => techDomain.id === domain.id)?.subdomains || [];
            const isSubdomainsExpanded = expandedSubdomains[domain.id] || false;

            return (
              <React.Fragment key={`${domain.id}-${index}`}>
                {index > 0 && <div className="h-px border-t border-gray-300"></div>}
                <div className="flex flex-col p-2">
                  <div className="flex flex-row gap-2 items-center min-w-0">
                    <div className="shrink-0">
                      <Score
                        score={trl}
                        size="small"
                        design="box"
                        type="TRL"
                      />
                    </div>

                    <TextWithLabel
                      title={domain.name}
                      label="Domain"
                      truncate
                      badge={
                        index === 0 ? (
                          <Badge
                            size="xs"
                            variant="success"
                          >
                            Primary
                          </Badge>
                        ) : undefined
                      }
                      className="text-sm"
                    />
                  </div>

                  {subdomains.length > 0 && (
                    <div
                      className="pl-[52px] flex justify-between items-center cursor-pointer mt-2"
                      onClick={() => toggleSubdomains(domain.id)}
                    >
                      <div className="text-xs font-normal text-[#1F1C1AB2]">
                        {subdomains.length} Subdomain{subdomains.length === 1 ? '' : 's'}
                      </div>
                      <ChevronDownIcon
                        size={20}
                        className={cn(
                          'text-[#1F1C1AB2] transition-transform duration-200',
                          isSubdomainsExpanded && 'rotate-180',
                        )}
                      />
                    </div>
                  )}

                  {isSubdomainsExpanded && subdomains.length > 0 && (
                    <div className="mt-2 flex flex-col gap-0.5">
                      {subdomains.map((subdomain, subIndex) => (
                        <div
                          key={subIndex}
                          className="flex flex-row items-center"
                        >
                          <div className="pl-[52px] flex flex-col">
                            <div className="text-gray-600 font-light text-xs">{subdomain.name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </>
      )}
    </div>
  );
};
